import { useContext, useEffect, useMemo, useRef } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { SocketTopics } from "@shared/types";
import { SocketArgs, SocketResultWrapper } from "@shared/services/socket/types";
import { usePrevious } from "@shared/utils/hooks/misc";
import { SocketContext } from "@shared/features/socket/Socket.context";
import {
  checkInitialDataArgsChanged,
  checkPnlArgsChanged,
  checkPricesArgsChanged,
} from "@shared/features/socket/utils";
import { socketsSlices } from "@shared/slices/sockets.slice";
import { useSelector } from "react-redux";

const useSocket = <Topic extends SocketTopics>(
  topic: Topic,
  args: SocketArgs<Topic> | typeof skipToken,
  delaySubscribe?: boolean
): SocketResultWrapper<Topic> | undefined => {
  const { socketHandler } = useContext(SocketContext)!;
  const subscribedArgsRef = useRef<SocketArgs<Topic> | typeof emptyArgs>(
    emptyArgs
  );
  const pendingSubscriptionArgsRef = useRef<
    SocketArgs<Topic> | typeof emptyArgs
  >(emptyArgs);
  const previousArgs = usePrevious(args);
  const dataSelector = useMemo(() => {
    if (args === skipToken) return () => undefined;
    return socketsSlices[topic].selectors.getArgsDataSelector(args);
  }, [args]);
  const value: SocketResultWrapper<Topic> | undefined =
    useSelector(dataSelector);

  const argsChanged = useMemo(() => {
    if (args === skipToken) return false;
    else if (previousArgs === skipToken) return true;
    else if (topic in TOPIC_TO_CHECK_ARGS_CHANGED) {
      return !!TOPIC_TO_CHECK_ARGS_CHANGED[topic]?.(previousArgs, args);
    }
    return previousArgs !== args;
  }, [previousArgs, args]);

  const handleUnsubscribe = () => {
    if (subscribedArgsRef.current !== emptyArgs) {
      socketHandler.unSubscribe(topic, subscribedArgsRef.current);
      subscribedArgsRef.current = emptyArgs;
    }
  };

  const unsubscribe = (withoutDelay?: boolean) => {
    if (withoutDelay) {
      handleUnsubscribe();
    } else {
      return setTimeout(() => {
        handleUnsubscribe();
      }, 500);
    }
  };

  const handleSubscribe = (argsToSubscribe: SocketArgs<Topic>) => {
    if (subscribedArgsRef.current !== emptyArgs) {
      unsubscribe(true);
    }
    socketHandler.subscribe(topic, argsToSubscribe);
    subscribedArgsRef.current = argsToSubscribe;
    pendingSubscriptionArgsRef.current = emptyArgs;
  };

  const subscribe = () => {
    if (args === skipToken) return;

    pendingSubscriptionArgsRef.current = args;
    if (delaySubscribe) {
      return setTimeout(() => {
        handleSubscribe(args);
      }, 500);
    } else {
      handleSubscribe(args);
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    const pendingSubscriptionArgs = pendingSubscriptionArgsRef.current;
    const subscribedArgs = subscribedArgsRef.current;
    const isPendingSubscription = pendingSubscriptionArgs !== emptyArgs;
    const alreadySubscribed = subscribedArgs !== emptyArgs;

    if (
      args !== skipToken &&
      ((!alreadySubscribed && !isPendingSubscription) || argsChanged)
    ) {
      timeout = subscribe();
    } else if (args === skipToken && alreadySubscribed) {
      timeout = unsubscribe();
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }

      if (pendingSubscriptionArgsRef.current === args) {
        pendingSubscriptionArgsRef.current = emptyArgs;
      }
    };
  }, [args]);

  useEffect(() => {
    return () => {
      handleUnsubscribe();
    };
  }, []);

  return value;
};

export default useSocket;

export const emptyArgs = Symbol.for("empty_args");

const TOPIC_TO_CHECK_ARGS_CHANGED: {
  [key in SocketTopics]?: any;
} = {
  [SocketTopics.PRICES]: checkPricesArgsChanged,
  [SocketTopics.GRAPHS_DATA]: checkPricesArgsChanged,
  [SocketTopics.AGG_TRADES]: checkInitialDataArgsChanged,
  [SocketTopics.AGG_BOTS]: checkInitialDataArgsChanged,
  [SocketTopics.PNL]: checkPnlArgsChanged,
};
